import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import emailService from "../services/emailService";
import clientService from "../services/clientService";
import { useSelectedClient } from "../context/SelectedClientContext";
import EmailListView from "../components/EmailListView";
import ClientSelector from "../components/ClientSidebar";
import "../css/design-tokens.css";
import "../css/Invoices.css";
import "../css/ListView.css";

const Member = () => {
    const [emails, setEmails] = useState([]);
    const [filteredEmails, setFilteredEmails] = useState([]);
    const [errorInfo, setErrorInfo] = useState({ show: false, message: "", type: "", details: "" });
    const [searchQuery, setSearchQuery] = useState("");
    const [clientSearchQuery, setClientSearchQuery] = useState("");
    const [clients, setClients] = useState([]);
    const [isLoadingEmails, setIsLoadingEmails] = useState(false);
    // Filter state - which filters are active
    const [activeFilters, setActiveFilters] = useState({
        hasInvoice: false,
        needsReview: false,
        thisWeek: false
    });
    // Mobile filter bar collapse state
    const [isFilterExpanded, setIsFilterExpanded] = useState(false);
    const { selectedClient } = useSelectedClient();
    const navigate = useNavigate();

    // Pagination state
    const [emailPage, setEmailPage] = useState(1);
    const [emailTotalPages, setEmailTotalPages] = useState(1);
    const ITEMS_PER_PAGE = 20;

    // Fetch emails when client or page changes
    useEffect(() => {
        const fetchEmails = async () => {
            if (!selectedClient) {
                console.log("📧 No client selected, clearing email list");
                setEmails([]);
                setFilteredEmails([]);
                return;
            }

            setIsLoadingEmails(true);
            try {
                console.log(`📧 Fetching paginated emails for client ID: ${selectedClient}...`);
                const data = await emailService.getPaginatedEmails(emailPage, ITEMS_PER_PAGE, selectedClient);

                console.log(`📧 Fetched paginated emails: ${data.emails?.length} emails for client ${selectedClient}`);
                setEmailTotalPages(data.totalPages || 1);

                // Normalize the fetched emails - include Invoices array from backend
                // Note: Backend already filters by clientId, no need to filter again here
                const normalizedEmails = data.emails.map(email => ({
                        id: email.id,
                        subject: email.subject || "No Subject",
                        body: email.body || "No Body",
                        rawMimeContent: email.rawMimeContent || "",
                        rawEmailData: email.rawEmailData || {},
                        attachments: email.attachments || [],
                        isReviewed: email.isReviewed || false,
                        createdAt: new Date(email.createdAt).toLocaleString(),
                        createdAtRaw: email.createdAt, // Keep raw date for filtering
                        updatedAt: new Date(email.updatedAt).toLocaleString(),
                        clientId: email.clientId,
                        senderEmail: email.senderEmail,
                        comment: email.comment || "",
                        Invoices: email.Invoices || [], // Include invoices from backend
                    }));

                console.log(`📧 Normalized ${normalizedEmails.length} emails for client ${selectedClient}`);
                setEmails(normalizedEmails);
                setFilteredEmails(normalizedEmails);
            } catch (error) {
                console.error("❌ Error fetching emails:", error);
                setErrorInfo({
                    show: true,
                    message: "Error Loading Tasks",
                    type: "error",
                    details: `Failed to retrieve tasks: ${error.message || "Unknown error"}`,
                });
            } finally {
                setIsLoadingEmails(false);
            }
        };

        fetchEmails();
    }, [selectedClient, emailPage]);

    // Fetch clients
    useEffect(() => {
        const fetchClients = async () => {
            try {
                const allClients = await clientService.getAllClients();

                // Custom sorting function for client names
                const sortedClients = allClients.sort((a, b) => {
                    const nameA = a.name.trim();
                    const nameB = b.name.trim();

                    // Helper function to find the first non-special character
                    const findFirstNonSpecialChar = (str) => {
                        for (let i = 0; i < str.length; i++) {
                            const char = str[i];
                            if (/[a-zA-Z0-9]/.test(char)) {
                                return char.toLowerCase();
                            }
                        }
                        return null; // No non-special characters found
                    };

                    const charA = findFirstNonSpecialChar(nameA);
                    const charB = findFirstNonSpecialChar(nameB);

                    // If both have valid non-special characters, compare them
                    if (charA && charB) {
                        return charA.localeCompare(charB);
                    }

                    // If only one has a valid non-special character, prioritize it
                    if (charA) return -1;
                    if (charB) return 1;

                    // If neither has a valid non-special character, keep original order
                    return 0;
                });

                console.log("✅ Clients fetched successfully:", sortedClients.length);
                setClients(sortedClients);
            } catch (error) {
                console.error("Error fetching clients:", error);
                setErrorInfo({
                    show: true,
                    message: "Error Loading Clients",
                    type: "error",
                    details: `Failed to retrieve clients: ${error.message || "Unknown error"}`,
                });
            }
        };

        fetchClients();
    }, []);

    // Reset page to 1 when filters change (prevents empty page 2 issue)
    useEffect(() => {
        setEmailPage(1);
    }, [activeFilters, searchQuery]);

    // Apply filters and search to emails
    useEffect(() => {
        let filtered = [...emails];

        // Apply search filter
        if (searchQuery.trim() !== '') {
            const searchTerm = searchQuery.toLowerCase();
            filtered = filtered.filter((email) =>
                (email.senderEmail || '').toLowerCase().includes(searchTerm) ||
                (email.subject || '').toLowerCase().includes(searchTerm) ||
                (email.comment || '').toLowerCase().includes(searchTerm)
            );
        }

        // Apply hasInvoice filter
        if (activeFilters.hasInvoice) {
            filtered = filtered.filter(email => email.Invoices && email.Invoices.length > 0);
        }

        // Apply needsReview filter
        if (activeFilters.needsReview) {
            filtered = filtered.filter(email => !email.isReviewed);
        }

        // Apply thisWeek filter
        if (activeFilters.thisWeek) {
            const oneWeekAgo = new Date();
            oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
            filtered = filtered.filter(email => {
                const emailDate = new Date(email.createdAtRaw);
                return emailDate >= oneWeekAgo;
            });
        }

        setFilteredEmails(filtered);
    }, [emails, searchQuery, activeFilters]);

    // Toggle a filter
    const toggleFilter = (filterName) => {
        setActiveFilters(prev => ({
            ...prev,
            [filterName]: !prev[filterName]
        }));
    };

    // Clear all filters
    const clearFilters = () => {
        setActiveFilters({
            hasInvoice: false,
            needsReview: false,
            thisWeek: false
        });
        setSearchQuery('');
    };

    // Check if any filter is active
    const hasActiveFilters = activeFilters.hasInvoice || activeFilters.needsReview || activeFilters.thisWeek || searchQuery.trim() !== '';

    // Handle marking email as reviewed - with OPTIMISTIC UI update
    const handleMarkEmailAsReviewed = async (emailId) => {
        // Find the current email to get its current status
        const currentEmail = emails.find(e => e.id === emailId);
        if (!currentEmail) return;

        const previousStatus = currentEmail.isReviewed;
        const newStatus = !previousStatus;

        // OPTIMISTIC UPDATE: Update UI immediately for instant feedback
        setEmails((prevEmails) =>
            prevEmails.map((email) =>
                email.id === emailId ? { ...email, isReviewed: newStatus } : email
            )
        );

        console.log(`📧 Optimistically toggling review status for email ID: ${emailId} to ${newStatus ? 'reviewed' : 'unreviewed'}...`);

        try {
            // Call the service to toggle review status in background
            await emailService.toggleReviewStatus(emailId);
            console.log(`✅ Server confirmed review status for email ${emailId}`);
        } catch (error) {
            // ROLLBACK: Revert to previous status if API call fails
            console.error("❌ Error toggling email review status, rolling back:", error);
            setEmails((prevEmails) =>
                prevEmails.map((email) =>
                    email.id === emailId ? { ...email, isReviewed: previousStatus } : email
                )
            );
            setErrorInfo({
                show: true,
                message: "Error Updating Task",
                type: "error",
                details: `Failed to update task: ${error.message || "Unknown error"}`,
            });
        }
    };

    // Handle adding a comment to an email
    const handleAddEmailComment = async (emailId, commentText) => {
        try {
            const updatedEmail = await emailService.addComment(emailId, commentText);

            // Update emails - filteredEmails will auto-update via useEffect
            setEmails((prevEmails) =>
                prevEmails.map((email) =>
                    email.id === emailId
                        ? { ...email, comment: updatedEmail.comment }
                        : email
                )
            );

            console.log(`Added comment to email ${emailId}`);
        } catch (error) {
            console.error("❌ Error adding comment to email:", error);
            setErrorInfo({
                show: true,
                message: "Error Adding Comment",
                type: "error",
                details: `Failed to add comment: ${error.message || "Unknown error"}`,
            });
        }
    };

    // Handle updating a comment on an email
    const handleUpdateEmailComment = async (emailId, commentText) => {
        try {
            const updatedEmail = await emailService.updateComment(emailId, commentText);

            // Update emails - filteredEmails will auto-update via useEffect
            setEmails((prevEmails) =>
                prevEmails.map((email) =>
                    email.id === emailId
                        ? { ...email, comment: updatedEmail.comment }
                        : email
                )
            );

            console.log(`Updated comment on email ${emailId}`);
        } catch (error) {
            console.error("❌ Error updating comment on email:", error);
            setErrorInfo({
                show: true,
                message: "Error Updating Comment",
                type: "error",
                details: `Failed to update comment: ${error.message || "Unknown error"}`,
            });
        }
    };

    // Handle page change
    const handleEmailPageChange = (newPage) => {
        setEmailPage(newPage);
        window.scrollTo(0, 0);
    };

    // Handle clicking on invoice badge - navigate to invoice view
    const handleViewInvoice = (invoiceId) => {
        navigate(`/view?id=${invoiceId}`);
    };

    return (
        <div className="member-container">
            {errorInfo.show && (
                <div className={`notification ${errorInfo.type}`}>
                    <h4>{errorInfo.message}</h4>
                    <p>{errorInfo.details}</p>
                    <button className="close-btn" onClick={() => setErrorInfo({ show: false, message: "", type: "", details: "" })}>×</button>
                </div>
            )}
            <div className="member-layout">
                <div className="content-area">
                    {/* Filter Bar with Client Selector */}
                    <div className={`filter-bar ${isFilterExpanded ? 'expanded' : 'collapsed'}`}>
                        {/* Mobile Toggle Button */}
                        <button
                            className="filter-toggle-btn"
                            onClick={() => setIsFilterExpanded(!isFilterExpanded)}
                        >
                            <span className="filter-toggle-icon">{isFilterExpanded ? '✕' : '☰'}</span>
                            <span className="filter-toggle-text">
                                {isFilterExpanded ? 'Close Filters' : 'Filters'}
                                {hasActiveFilters && !isFilterExpanded && <span className="filter-active-dot"></span>}
                            </span>
                        </button>

                        {/* Filter Content - always visible on desktop, toggleable on mobile */}
                        <div className="filter-content">
                            {/* Client Selector - integrated into filter bar */}
                            <ClientSelector
                                clients={clients}
                                clientSearchQuery={clientSearchQuery}
                                setClientSearchQuery={setClientSearchQuery}
                            />

                            <div className="filter-divider"></div>

                            <button
                                className={`filter-chip ${!hasActiveFilters ? 'active' : ''}`}
                                onClick={clearFilters}
                            >
                                All
                            </button>

                            <button
                                className={`filter-chip ${activeFilters.hasInvoice ? 'active' : ''}`}
                                onClick={() => toggleFilter('hasInvoice')}
                            >
                                <span className="filter-chip-icon">💰</span>
                                Has Invoice
                            </button>

                            <button
                                className={`filter-chip ${activeFilters.needsReview ? 'active' : ''}`}
                                onClick={() => toggleFilter('needsReview')}
                            >
                                <span className="filter-chip-icon">⚠️</span>
                                Needs Review
                            </button>

                            <button
                                className={`filter-chip ${activeFilters.thisWeek ? 'active' : ''}`}
                                onClick={() => toggleFilter('thisWeek')}
                            >
                                <span className="filter-chip-icon">📅</span>
                                This Week
                            </button>

                            {/* Search input */}
                            <input
                                type="text"
                                className="filter-search"
                                placeholder="Search tasks..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                aria-label="Search tasks"
                            />

                            {hasActiveFilters && (
                                <button className="filter-clear" onClick={clearFilters}>
                                    Clear all
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Task List */}
                    <div className="list-view-container">
                        {selectedClient ? (
                            isLoadingEmails ? (
                                <div className="list-view">
                                    <h3>Tasks</h3>
                                    {/* Loading Skeleton - wrapped in ul for consistent layout */}
                                    <ul className="email-list">
                                        {[1, 2, 3, 4, 5].map((i) => (
                                            <li key={i} className="task-skeleton">
                                                <div className="task-skeleton-row">
                                                    <div className="skeleton task-skeleton-checkbox"></div>
                                                    <div className="skeleton task-skeleton-title"></div>
                                                    <div className="skeleton task-skeleton-badge"></div>
                                                    <div className="skeleton task-skeleton-action"></div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ) : (
                                <EmailListView
                                    title="Tasks"
                                    emails={filteredEmails}
                                    onMarkAsReviewed={handleMarkEmailAsReviewed}
                                    onAddComment={handleAddEmailComment}
                                    onUpdateComment={handleUpdateEmailComment}
                                    onViewInvoice={handleViewInvoice}
                                    currentPage={emailPage}
                                    totalPages={emailTotalPages}
                                    onPageChange={handleEmailPageChange}
                                />
                            )
                        ) : (
                            <div className="select-client-prompt">
                                <div className="prompt-icon">👆</div>
                                <h3>Select a client to view tasks</h3>
                                <p>Use the client dropdown above to get started</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Member;
